import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  ArrowsClockwise,
  ChatCircle,
  Clock,
  CreditCard,
  DeviceMobile,
  Envelope,
  CheckSquare,
  User,
} from '@phosphor-icons/react';
import { RowValue, SettingCard, SettingRow } from '../../../../../../components/settings/setting-card';
import { ValueEditorDialog } from '../../../../../../components/settings/value-editor-dialog';
import { Toggle } from '../../../../../../components/ui/toggle';
import type { TeamNotificationRules } from '@mybusinessmate/schema';
import { Skeleton } from '../../../../../../components/ui/skeleton';
import { useSectionPatch } from '../../../../../../lib/workspace-api';

export const Route = createFileRoute('/subdomain/admin/_staff/settings/workspace/team-notifications')({
  component: TeamNotificationsPage,
});

type TextField = 'digestRecipients' | 'starterEmail' | 'starterPush' | 'starterQuietHours';
type ToggleField = 'paymentFailed' | 'integrationFailing' | 'taskIdle' | 'clientSilent' | 'digestEnabled';

const TEXT: Record<TextField, { title: string; label: string; placeholder?: string }> = {
  digestRecipients: {
    title: 'Who gets the daily ops digest',
    label: 'Names or emails (separate with commas)',
    placeholder: 'Priya S., Sana A.',
  },
  starterEmail: {
    title: 'Email for new staff',
    label: 'What new teammates get by email',
    placeholder: 'Assigned, reviews, meetings',
  },
  starterPush: {
    title: 'Push for new staff',
    label: 'What new teammates get as push alerts',
    placeholder: 'Everything',
  },
  starterQuietHours: { title: 'Quiet hours for new staff', label: 'When alerts pause', placeholder: '9 PM – 8 AM' },
};

function TeamNotificationsPage() {
  const { data: rules, patch } = useSectionPatch('teamNotifications');
  if (!rules) return <Skeleton className="h-(--mbm-layout-dialog-wide-width) rounded-(--mbm-radius-lg)" />;
  return <TeamNotificationsForm rules={rules} patch={patch} />;
}

function TeamNotificationsForm({
  rules,
  patch,
}: {
  rules: TeamNotificationRules;
  patch: (changes: Partial<TeamNotificationRules>) => void;
}) {
  const [editing, setEditing] = useState<TextField>();

  const toggle = (field: ToggleField, label: string) => (
    <Toggle
      aria-label={label}
      checked={rules[field]}
      onCheckedChange={(on) => patch({ [field]: on } as Partial<TeamNotificationRules>)}
    />
  );

  return (
    <>
      <SettingCard
        title="Alerts for admins"
        subtitle="Problems that need someone senior. Sent to admins, plus the client’s account manager where it makes sense."
      >
        <SettingRow
          icon={CreditCard}
          title="A payment failed or a plan was cancelled"
          subtitle="Admins + that client’s account manager"
          trailing={toggle('paymentFailed', 'A payment failed or a plan was cancelled')}
        />
        <SettingRow
          icon={ArrowsClockwise}
          title="An integration or webhook keeps failing"
          subtitle="After 3 failures in an hour"
          trailing={toggle('integrationFailing', 'An integration or webhook keeps failing')}
        />
        <SettingRow
          icon={CheckSquare}
          title="A task has had nobody on it for 2 days"
          subtitle="Admins + account manager"
          trailing={toggle('taskIdle', 'A task has had nobody on it for 2 days')}
        />
        <SettingRow
          icon={ChatCircle}
          title="A client hasn’t replied for 7 days"
          subtitle="Account manager only"
          trailing={toggle('clientSilent', 'A client hasn’t replied for 7 days')}
        />
      </SettingCard>

      <SettingCard
        title="Daily ops digest"
        subtitle="One email each morning: new requests, overdue reviews, meetings today and anything stuck."
      >
        <SettingRow
          icon={Envelope}
          title="Send the digest at 9:00 AM"
          subtitle="Skipped on Sundays"
          trailing={toggle('digestEnabled', 'Send the digest at 9:00 AM')}
        />
        <SettingRow
          icon={User}
          title="Who gets it"
          trailing={<RowValue>{rules.digestRecipients || 'Admins'}</RowValue>}
          onClick={() => setEditing('digestRecipients')}
        />
      </SettingCard>

      <SettingCard
        title="Starting setup for new staff"
        subtitle="What new teammates get before they change it themselves in Notifications."
      >
        <SettingRow
          icon={Envelope}
          title="Email"
          trailing={<RowValue>{rules.starterEmail}</RowValue>}
          onClick={() => setEditing('starterEmail')}
        />
        <SettingRow
          icon={DeviceMobile}
          title="Push"
          trailing={<RowValue>{rules.starterPush}</RowValue>}
          onClick={() => setEditing('starterPush')}
        />
        <SettingRow
          icon={Clock}
          title="Quiet hours"
          trailing={<RowValue>{rules.starterQuietHours}</RowValue>}
          onClick={() => setEditing('starterQuietHours')}
        />
      </SettingCard>

      {editing ? (
        <ValueEditorDialog
          open
          onClose={() => setEditing(undefined)}
          title={TEXT[editing].title}
          label={TEXT[editing].label}
          editor={{ kind: 'text', value: rules[editing], placeholder: TEXT[editing].placeholder, maxLength: 120 }}
          onSave={(v) => patch({ [editing]: String(v) } as Partial<TeamNotificationRules>)}
        />
      ) : null}
    </>
  );
}
